import { G } from '@mobily/ts-belt';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Show } from '@react-admin/ra-rbac';
import { get } from 'lodash';
import { createContext, JSX, useEffect, useState } from 'react';
import {
  RaRecord,
  ShowProps,
  useNotify,
  useRecordContext,
  useResourceDefinition,
  useSidebarState,
  useTranslate,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';

import { Avatar } from '@sh4res/bo-common/components/Avatar';
import {
  CommentsButton,
  CommentsProps,
} from '@sh4res/bo-common/components/Comments/CommentsButton';
import { LogsButton, LogsProps } from '@sh4res/bo-common/components/Logs/LogsButton';
import { PageTitle } from '@sh4res/bo-common/components/PageTitle';
import { RightPanel, RightPanelTab } from '@sh4res/bo-common/components/RightPanel';
import { Resources } from '@sh4res/bo-common/utils';
import { buildLink } from '@sh4res/bo-common/utils/build_link';

const CustomAvatar = (props: Pick<ShowWithNavigationProps, 'avatar'>) => {
  const record = useRecordContext<RaRecord>();
  if (!props.avatar) {
    return null;
  }

  const { name_source, image_source } = props.avatar;
  const name = String(get(record, name_source));
  const image = image_source ? get(record, image_source) : undefined;

  return (
    <Avatar
      style={{
        width: '36px',
        height: '36px',
        marginRight: '10px',
      }}
      fullName={name}
      src={image}
    />
  );
};

export const CustomTitle = ({
  title,
  avatar,
  navigateTo,
  logs,
  comments,
  setShowRightPanel,
  showRightPanel,
  titleActions,
  record: raRecord,
  hideBackBtn,
  subtitle,
}: Pick<
  ShowWithNavigationProps,
  | 'title'
  | 'avatar'
  | 'navigateTo'
  | 'logs'
  | 'comments'
  | 'titleActions'
  | 'hideBackBtn'
  | 'subtitle'
> & {
  setShowRightPanel: (tab: RightPanelTab) => void;
  showRightPanel: RightPanelTab | null;
  record?: RaRecord;
}) => {
  const translate = useTranslate();
  const record = useRecordContext<RaRecord>(raRecord);
  const resource = useResourceDefinition();
  const navigate = useNavigate();
  const notify = useNotify();

  const titleRemoved = typeof title === 'string' && title.trim() === '';
  if (!record || titleRemoved) {
    return null;
  }

  const customTitle = typeof title === 'string' ? translate(title, { smart_count: 1 }) : title;
  const isNavigateBack = navigateTo?.(record) === -1;

  const onClick = () => {
    const navigateToLoc = navigateTo?.(record);
    if (navigateToLoc !== undefined && typeof navigateToLoc === 'string') {
      navigate(navigateToLoc);
    } else if (isNavigateBack) {
      navigate(-1);
    } else {
      navigate(buildLink(resource.name as Resources, 'list'));
    }
  };

  const onCopyId = async () => {
    await navigator.clipboard.writeText(String(record.id));
    notify('resources.bo_common.accessibility.copied', { type: 'info' });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        marginRight: showRightPanel ? '375px' : undefined,
      }}>
      {(resource.hasList || isNavigateBack) && !hideBackBtn && (
        <IconButton
          onClick={onClick}
          edge="start"
          aria-label={translate('resources.bo_common.accessibility.back')}
          sx={{ height: '26px', color: 'black', backgroundColor: 'transparent !important;' }}
          data-testid="BoExternalShowWithNavigation_BackButton">
          <ChevronLeftIcon sx={{ fontSize: '23px', marginTop: '10px' }} />
        </IconButton>
      )}
      <CustomAvatar avatar={avatar} />
      <Box sx={{ display: 'grid', minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            fontWeight: 600,
            fontSize: '17px',
            margin: 0,
            lineHeight: '1.3',
          }}>
          {customTitle ?? translate(resource.options?.label ?? resource.name)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            sx={{
              color: 'text.secondary',
              fontWeight: 400,
              fontSize: '12px',
              margin: 0,
              lineHeight: '1.3',
            }}>
            {subtitle ? subtitle(record) : `ID #${record.id}`}
          </Typography>
          <Tooltip
            title={translate('resources.bo_common.accessibility.copy_id')}
            enterDelay={600}
            enterNextDelay={600}>
            <IconButton
              size="small"
              onClick={onCopyId}
              aria-label={translate('resources.bo_common.accessibility.copy_id')}
              sx={{ padding: '2px', marginLeft: '2px', color: 'text.secondary' }}
              data-testid="BoExternalShowWithNavigation_CopyIdButton">
              <ContentCopyIcon sx={{ fontSize: '12px' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box flex={1} />
      {titleActions}
      {!showRightPanel && G.isNotNullable(comments) ? (
        <CommentsButton
          {...comments}
          onOpen={() => setShowRightPanel('comments')}
          data-testid="BoExternalShowWithNavigation_CommentsButton"
        />
      ) : null}
      {!showRightPanel && G.isNotNullable(logs) ? (
        <LogsButton
          {...logs}
          onOpen={() => setShowRightPanel('logs')}
          data-testid="BoExternalShowWithNavigation_LogsButton"
        />
      ) : null}
    </Box>
  );
};

export const RightPanelContext = createContext({ isOpen: false });
type ShowWithNavigationProps = ShowProps & {
  navigateTo?: (record: RaRecord) => string | -1;
  avatar?: { image_source?: string; name_source: string };
  logs?: LogsProps;
  titleActions?: JSX.Element;
  subtitle?: (record: RaRecord) => string;
  comments?: CommentsProps;
  button?: JSX.Element;
  hideBackBtn?: boolean;
  pageTitle?: string;
};
export const ShowWithNavigation = ({
  navigateTo,
  title,
  subtitle,
  avatar,
  sx,
  logs,
  comments,
  titleActions,
  children,
  button,
  hideBackBtn,
  pageTitle,
  ...other
}: ShowWithNavigationProps) => {
  const [showRightPanel, setShowRightPanel] = useState<RightPanelTab | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useSidebarState();
  useEffect(() => {
    if (isSidebarOpen) {
      setShowRightPanel(null);
    }
  }, [isSidebarOpen, setIsSidebarOpen]);

  return (
    <RightPanelContext.Provider value={{ isOpen: !!showRightPanel }}>
      <PageTitle title={pageTitle} />
      <Show
        component={'div'}
        sx={{
          marginRight: showRightPanel ? '400px' : undefined,
          gridTemplateRows: 'min-content min-content',
          '.RaShow-card': {
            display: 'grid',
          },
          ...sx,
        }}
        {...other}
        title={
          <CustomTitle
            hideBackBtn={hideBackBtn}
            title={title}
            subtitle={subtitle}
            navigateTo={navigateTo}
            avatar={avatar}
            logs={logs}
            comments={comments}
            titleActions={titleActions}
            setShowRightPanel={(value: RightPanelTab) => {
              setShowRightPanel(value);
              setIsSidebarOpen(false);
            }}
            showRightPanel={showRightPanel}
          />
        }>
        {G.isNotNullable(button) ? (
          <>
            <Box sx={{ overflow: 'hidden', height: '100%' }}>{children}</Box>
            <Box
              sx={{
                position: 'fixed',
                bottom: '40px',
                zIndex: 5,
                right: showRightPanel ? '475px' : '75px',
                '& .MuiButton-root': {
                  height: '30px',
                },
              }}>
              {button}
            </Box>
          </>
        ) : (
          children
        )}
        {showRightPanel && (
          <RightPanel
            comments={comments}
            logs={logs}
            tab={showRightPanel}
            onClose={() => {
              setShowRightPanel(null);
              setIsSidebarOpen(true);
            }}
          />
        )}
      </Show>
    </RightPanelContext.Provider>
  );
};
